export default {
  id: 'trade-analysis',
  title: 'Trade Analysis',
  content: `## Trade Analysis

Trade Analysis is a deep-dive into your complete trading history — not just P&L, but the patterns behind your results.

---

## Where to Find It

**Profile → Trade Analysis**

---

## Overview Section

The overview shows your all-time and period-filtered performance at a glance:

| Metric | Description |
|---|---|
| Total trades | All completed trades (entry + exit) |
| Win rate | % of trades that closed in profit |
| Avg win (₹) | Average profit on winning trades |
| Avg loss (₹) | Average loss on losing trades |
| Profit factor | Total wins ÷ total losses |
| Avg hold time | Average time from entry to exit |
| Best trade | Highest single-trade profit |
| Worst trade | Largest single-trade loss |
| Total realised P&L | Net profit across all closed trades |

---

## P&L Breakdown

A chart showing your cumulative P&L over time. Look for:
- **Consistent upward slope** — steady compounding of disciplined decisions
- **Flat periods** — times when you stopped trading or were breaking even
- **Sharp drops** — single large losses or a cluster of losses — investigate what happened

---

## Win Rate by Category

Your win rate is broken down by multiple dimensions:

| Dimension | Why it matters |
|---|---|
| By trade type (INTRADAY vs DELIVERY) | Tells you which style suits you better |
| By source (MANUAL vs ALGO_FLOW vs NYX_AI) | Compares your manual judgment to automated strategies |
| By sector | Reveals which sectors you trade best in |
| By scanner signal | Shows which signal types produce your best results |
| By day of week | Identifies if you trade worse on specific days (e.g., Mondays, Fridays) |

---

## Avg Win vs Avg Loss

This is the most important comparison after win rate.

**Example:**
> Win rate: 45% | Avg win: ₹8,500 | Avg loss: ₹4,200

Even with a 45% win rate, the average win is 2× the average loss. This is a profitable profile.

> Win rate: 60% | Avg win: ₹3,000 | Avg loss: ₹6,000

60% win rate sounds good — but average loss is 2× average win. This is a losing profile over time.

---

## Behavioral Flags in Trade Analysis

Trade Analysis surfaces behavioral patterns detected across your history:

| Flag | What it shows |
|---|---|
| Stop avoidance | Trades where you were in loss beyond your stop level before eventually exiting |
| Early exits | Trades where you exited before target with profit still available |
| Revenge trades | Low-quality trades placed within 30 minutes of a loss closing |
| Oversize positions | Trades where the implied risk exceeded your Risk Per Trade % |
| Best day / Worst day | Identifies specific dates with unusual P&L — investigate what you did differently |

---

## Time Period Filters

Filter the analysis by:
- Last 7 days
- Last 30 days
- Last 90 days
- Custom date range
- All time

Use 30-day windows to spot recent trends. Use all-time to see your overall trajectory since joining TradeLab.`,
};
